import { Datakatalog } from '@/bootstrap/data';
import { Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { VegobjektEgenskapKvalitetAttributt } from '@/domain/vegobjekter/Vegobjekt';
import {
    API_ENVIRONMENT_STORAGE_KEY,
    API_VERSION_STORAGE_KEY,
    ApiEnvironment,
    ApiVersion,
    Role,
} from '@/state';
import { createLogger } from './Logger';
import { Config } from '@/bootstrap/config';
import { runtimeEnvironment } from '@/config';

export type Discriminate<A, B> =
    ({ [Property in keyof A]+?: never } & B) | ({ [Property in keyof B]+?: never } & A);

export function assertUnreachable(_: never): never {
    throw new Error('This should never happen because compiler will find this bug!');
}

export function formatNumber(numberToFormat: number): string {
    return numberToFormat.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1 ');
}

export function uppercaseFirstLetter(string: string): string {
    return string.substring(0, 1).toUpperCase() + string.substring(1);
}

export const isToday = (someDate: Date): boolean => {
    const today = new Date();
    return (
        someDate.getDate() == today.getDate() &&
        someDate.getMonth() == today.getMonth() &&
        someDate.getFullYear() == today.getFullYear()
    );
};

/**
 * A sort comparator for numbers
 * @param {number} a First number
 * @param {number} b Second number
 * @returns {number} comparison result. Negative if a should be sorted before b, positive if b should be sorted before
 * a, zero if they are equal.
 */
export function numericSortComparator(a: number, b: number): number {
    return a - b;
}

export function nullIfUndefined<T>(value: T) {
    return value === undefined ? null : value;
}

const log = createLogger('Integration');
export const tryTransform = <T, TResponse>(transformer: (response: TResponse) => T) => {
    return (response: string) => {
        let data = null;
        try {
            data = transformer(JSON.parse(response));
        } catch (e) {
            log('Error transforming {} {}', response, e);
            throw e;
        }
        if (!data) {
            log('Failed to transform {}', response);
            throw new Error('Failed to transform: ' + response);
        }
        return data;
    };
};

export function findContainer(
    element: HTMLElement | null,
    containerClass: string
): HTMLElement | null {
    if (element === null) return null;
    if (element.className.includes(containerClass)) {
        return element;
    } else {
        return findContainer(element.parentElement, containerClass);
    }
}

export function getHash(uriOrHash: string): string {
    // decodeURI because some browsers(FF) returns encoded hash.
    // ~(farge:'0_0,id:15) is returned as ~(farge:%270_0,id:15)
    return decodeURI(uriOrHash.substring(uriOrHash.indexOf('#') + 1));
}

/**
 * Sanitizes the current URL by removing any query parameters (the part before #).
 * This prevents arbitrary data from being injected into the URL.
 * Automatically redirects to the sanitized URL if query parameters are present.
 */
export function sanitizeUrl(): void {
    if (window.location.search !== '') {
        const { pathname, hash } = window.location;
        const cleanPathname = pathname.replace('//', '/');
        window.location.replace(cleanPathname + hash);
    }
}

export function isUrl(string: string) {
    try {
        const _ = new URL(string);
        return true;
    } catch {
        return false;
    }
}

export function isLinkQuery(text: string): boolean {
    /*
     Valid queries:
     @123456
     0.4@123456
     0.3-0.9@123456
     */

    return text.includes('@') && /^(\d\.?\d*-?){0,2}@\d+$/.test(text);
}

export function hasAccess(sensitivity: unknown, roles: Record<Role, boolean>): boolean {
    if (
        typeof sensitivity !== 'number' ||
        !Number.isInteger(sensitivity) ||
        sensitivity < 0 ||
        sensitivity > 3
    )
        throw Error('Unknown sensitivity code');
    return (
        sensitivity == 0 ||
        (sensitivity == 1 && roles[Role.Sensitiv1]) ||
        (sensitivity == 2 && roles[Role.Sensitiv2]) ||
        (sensitivity == 3 && roles[Role.Sensitiv3])
    );
}

export const download = (url: string, name?: string) => {
    const a = document.createElement('a');
    a.href = url;
    if (name) a.download = name;
    document.body.append(a);
    a.click();
    a.parentNode?.removeChild(a);
};

export function interpretQuality(
    q: VegobjektEgenskapKvalitetAttributt,
    datakatalog: Datakatalog
): string | number {
    const nvdbDoc = datakatalog.getTypeById(793).egenskapstypeById(9553);

    switch (q.name) {
        case 'synbarhet':
            const qualityDefinitions = nvdbDoc?.egenskapById(qualityEnumId[q.name]);
            const label =
                qualityDefinitions?.verdiByKortnavn(q.value.toString())?.verdi ??
                'Ugyldig verdi';
            return `${label} (${q.value})`;
        case 'nøyaktighet':
        case 'nøyaktighetHøyde':
        case 'maksimaltAvvik':
            if (typeof q.value === 'number' && q.value <= 0)
                return `Ikke registrert (${q.value})`;
            return `${q.value} cm`;
        case 'datafangstmetode':
        case 'datafangstmetodeHøyde':
            return typeof q.value === 'string' && q.value in datafangstMetode
                ? `${datafangstMetode[q.value]} (${q.value})`
                : q.value;
        default:
            return q.value;
    }
}

const datafangstMetode: Record<string, string> = {
    dig: 'Digitalisert',
    fot: 'Fotogrammetri',
    gen: 'Generert',
    lan: 'Landmålt',
    pla: 'Plandata',
    sat: 'Satellittmålt',
    byg: 'Som bygget',
    ukj: 'Ukjent',
};

const qualityEnumId = {
    synbarhet: 9545,
};

export const trapFocus = (element: HTMLElement) => {
    const focusableEls = element.querySelectorAll<HTMLElement>(
        'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
    );
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];
    const KEYCODE_TAB = 9;

    element.addEventListener('keydown', function (e: KeyboardEvent) {
        const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

        if (!isTabPressed || !firstFocusableEl || !lastFocusableEl) {
            return;
        }

        if (e.shiftKey) {
            if (document.activeElement === firstFocusableEl) {
                lastFocusableEl.focus();
                e.preventDefault();
            }
        } else {
            if (document.activeElement === lastFocusableEl) {
                firstFocusableEl.focus();
                e.preventDefault();
            }
        }
    });
};

// Modulo operator.
// % operator in js works as a 'reminder' and not 'modulo', causing issues with negative numbers
export const mod = (a: number, n: number): number => ((a % n) + n) % n;

export const getApiVersion = (): ApiVersion => {
    const apiVersion = sessionStorage.getItem(API_VERSION_STORAGE_KEY);
    switch (apiVersion) {
        case ApiVersion.V4:
            return ApiVersion.V4;
        case ApiVersion.V3:
            return ApiVersion.V3;
        default:
            return ApiVersion.V4;
    }
};

export const getDaKatApiURL = (config: Config): string => {
    switch (getApiVersion()) {
        case ApiVersion.V4:
            return `${config.restapi_url}/datakatalog/api/v1/vegobjekttyper`;
        default:
            return `${config.restapi_url_v3}/vegobjekttyper?inkluder=alle`;
    }
};

export const getApiURLArea = (config: Config): string => {
    switch (getApiVersion()) {
        case ApiVersion.V4:
            return `${config.restapi_url}/omrader/api/v4`;
        default:
            return `${config.restapi_url_v3}/omrader`;
    }
};

export const getApiURLVegobjekter = (config: Config): string => {
    switch (getApiVersion()) {
        case ApiVersion.V4:
            return `${config.vegobjekt_api_url}`;
        default:
            return `${config.restapi_url_v3}`;
    }
};

export const getApiURLVegnett = (config: Config): string => {
    switch (getApiVersion()) {
        case ApiVersion.V4:
            return `${config.vegnett_api_url}`;
        default:
            return `${config.restapi_url_v3}/vegnett`;
    }
};

export const isMobileDevice = () => {
    return /android|webos|iphone|ipad|ipod|opera mini/i.test(navigator.userAgent);
};
